<script lang="ts" setup>
import { storeToRefs } from "pinia";
import { CriteriaInstance } from "types/Checklist";
import { CommentPlacementLocation } from "types/enums/CommentPlacementLocation.enum";
import { useChecklistControllerStore } from "~/composables/useChecklistControllerStore";

defineEmits(["remove"]);

const { criteriaInstance } = defineProps({
  criteriaInstance: {
    type: Object as PropType<CriteriaInstance>,
    required: true,
  },
});

const documentId = inject("documentId") as any;

const documentViewModeStore = useDocumentViewModeStore(documentId);
const { isTeacherViewMode } = storeToRefs(documentViewModeStore);

const checklistStore = useDocumentChecklistStore(documentId);
const {
  checklistLevelId,
  checklistUnitNumber,
  useChecklist,
  evaluationMetrics,
} = storeToRefs(checklistStore);

const thesaurusStore = useDocumentThesaurusStore(documentId);
const { wordListById } = storeToRefs(thesaurusStore);

const resultsCriteriaStore = useResultsForCriteriaIdStore(
  documentId,
  criteriaInstance
);
const { updateManualPoints } = resultsCriteriaStore;
const {
  earnedPoints,
  hasNoScore,
  hasAISuggestions,
  requiresReview,
  reviewed,
  isReviewedByStudent,
  isIncomplete,
  isLoadingResults,
  relevantInstancesForParagraph,
  criteriaParagraphPositionsData,
} = storeToRefs(resultsCriteriaStore);

const { getCriteriaFromId } = checklistStore;

const title = computed(() => {
  if (
    criteriaInstance.isCustom &&
    criteriaInstance.labelOverride &&
    criteriaInstance.labelOverride != ""
  ) {
    return criteriaInstance.labelOverride;
  }

  return criteriaInstance.checklistCriteriaId
    ? getCriteriaFromId(criteriaInstance.checklistCriteriaId!)?.label
    : "N/A";
});

const { setCurrentChecklistCriteriaInstance, setScrollPosition } =
  useChecklistControllerStore(documentId);

const onClickCriteria = () => {
  if (isTeacherViewMode.value == true) {
    navigateToCriteria();
    return;
  }

  criteriaInstance.isStudentMarkedComplete =
    !criteriaInstance.isStudentMarkedComplete;
  checklistStore.saveChecklist();
};

const navigateToCriteria = () => {
  if (criteriaInstance.ignore == true) return;

  if (
    isTeacherViewMode.value != true &&
    checklistCriteria.value?.showFeedbackToStudent != true
  )
    return;

  if (criteriaInstance.isCustom == true) return;

  saveScrollPosition();
  setCurrentChecklistCriteriaInstance(criteriaInstance);
};

const saveScrollPosition = () => {
  const checklistContainer = document.getElementById("checklist-container");
  if (checklistContainer) {
    setScrollPosition(checklistContainer.scrollTop);
  }
};

const showInput = computed(() => isTeacherViewMode.value == true);

const updateScore = async (newScore: number) => {
  if (criteriaInstance.isCustom != true) {
    updateManualPoints(newScore);
  } else {
    let value = newScore;
    if (typeof value == "string") {
      value = parseInt(value);

      if (isNaN(value)) {
        return;
      }
    }

    criteriaInstance.earnedPoints = value;

    await checklistStore.saveChecklist();
  }
};

const awardFullPoints = () => {
  updateManualPoints(criteriaInstance.maxPoints);
};
const awardZeroPoints = () => {
  updateManualPoints(0);
};

const isDisabled = computed(() => {
  return criteriaInstance.evaluationMethod == "paragraph";
});

const checklistCriteria = computed(() => {
  if (criteriaInstance.checklistCriteriaId == undefined) return undefined;

  return checklistStore.getCriteriaFromId(criteriaInstance.checklistCriteriaId);
});

const allowQuickScore = computed(() => {
  if (criteriaInstance.checklistCriteriaId == undefined) return false;

  const checklistCriteria = checklistStore.getCriteriaFromId(
    criteriaInstance.checklistCriteriaId
  );

  if (!checklistCriteria) return false;

  return checklistCriteria.requiresReview;
});

const onClickInput = () => {
  if (isDisabled.value != true) return;

  navigateToCriteria();
};

const criteriaEarnedPoints = computed(() => {
  if (criteriaInstance.isCustom == true) return criteriaInstance.earnedPoints;

  return earnedPoints.value;
});

const allowIgnore = computed(() => {
  if (criteriaInstance.isCustom == true) return false;

  if (criteriaInstance.checklistCriteriaId == undefined) return false;

  const criteria = getCriteriaFromId(criteriaInstance.checklistCriteriaId);

  if (!criteria) return false;

  return criteria.allowIgnore;
});

const comments = computed(() => {
  if (criteriaInstance.checklistCriteriaId == undefined) return [];

  const criteria = getCriteriaFromId(criteriaInstance.checklistCriteriaId);

  if (!criteria) return [];

  if (!criteria.id) return [];

  if (!criteria.quickCommentTag) return criteria.quickComments ?? [];

  return filteredComments
    .value({
      tag: criteria.quickCommentTag,
      documentId: documentId,
    })
    .map((comment) => comment.comment);
});

const showQuickKey = computed(() => {
  if (criteriaInstance.checklistCriteriaId == undefined) return false;

  const criteria = getCriteriaFromId(criteriaInstance.checklistCriteriaId);

  if (!criteria) return false;

  if (criteria.commentPlacementLocation == undefined) return false;

  if (comments.value.length <= 0) return false;

  switch (criteria.commentPlacementLocation) {
    case CommentPlacementLocation.beforeDocument:
      return hasParagraphs.value;
    case CommentPlacementLocation.beforeTitle:
      return hasParagraphBeforeTitle.value;
    case CommentPlacementLocation.beforeConclusion:
      return hasParagraphBeforeConclusion.value;
  }
});

const systemCommentsStore = useSystemCommentsStore();
const { filteredComments } = storeToRefs(systemCommentsStore);

const criteria = computed(() => {
  if (!criteriaInstance.checklistCriteriaId) return undefined;

  return getCriteriaFromId(criteriaInstance.checklistCriteriaId);
});

const documentParagraphsStore = useDocumentParagraphsStore(documentId);
const { hasParagraphBeforeConclusion, hasParagraphBeforeTitle, hasParagraphs } =
  storeToRefs(documentParagraphsStore);

const isCriteriaComplete = computed(() => {
  if (isTeacherViewMode.value == true) {
    return reviewed.value == true;
  }

  return isReviewedByStudent.value == true;
});

const isAssignmentComponentCriteria = computed(() => {
  if (criteriaInstance.checklistCriteriaId == undefined) return false;

  const criteria = getCriteriaFromId(criteriaInstance.checklistCriteriaId);

  if (!criteria) return false;

  return criteria.isAssignmentComponentCriteria;
});

const showStudentInstructions = ref(false);
const toggleShowStudentInstructions = () => {
  showStudentInstructions.value = !showStudentInstructions.value;
};

const studentInstructions = computed(() => {
  if (criteriaInstance.checklistCriteriaId == undefined) return undefined;

  const criteria = getCriteriaFromId(criteriaInstance.checklistCriteriaId);

  if (!criteria) return undefined;

  const selfAssessmentSteps = criteria.selfAssessmentSteps;

  const instructions = (selfAssessmentSteps ?? [])
    .map((step) => {
      let instructions = step.instructions;

      if (
        step.instructionOverides != undefined &&
        step.instructionOverides.length > 0
      ) {
        const override = step.instructionOverides.find((o) => {
          let matchesLevel = true;

          if (
            checklistLevelId.value != undefined &&
            (o.levelIds ?? []).length > 0
          ) {
            matchesLevel = (o.levelIds ?? []).includes(checklistLevelId.value);
          }

          let matchesUnit = true;

          if (
            checklistUnitNumber.value != undefined &&
            (o.unitNumbers ?? []).length > 0
          ) {
            matchesUnit = (o.unitNumbers ?? []).includes(
              checklistUnitNumber.value
            );
          }

          return matchesLevel && matchesUnit;
        });

        if (override != undefined) {
          instructions = override.instructions;
        }
      }

      if (instructions == undefined || instructions == "") return undefined;

      return {
        step: step.label,
        instructions: instructions,
      };
    })
    .filter((notes) => {
      if (notes?.instructions == undefined) return false;

      if (notes.instructions.length == 0) return false;

      // Filter out quill objects with only one insert that contains a \n like this:
      // {insert: '\n'}

      if (notes.instructions.length == 1) {
        if (notes.instructions[0].insert == "\n" || notes.instructions[0].insert == "") {
          return false;
        }
      }

      return true;
    });

  if (instructions != undefined && instructions.length > 0) {
    return instructions;
  }

  // If there are no self assessment instrucitons check the criteria student notes
  const studentNotes = [
    {
      step: "Student Notes",
      instructions: criteria.studentInstructions,
    },
  ];

  // First ceck if there are overrides and if to use them
  const overrides = criteria.studentInstructionsOverrides;

  if (
    overrides != undefined &&
    overrides.length > 0 &&
    checklistLevelId.value != undefined
  ) {
    const override = overrides.find((o) => {
      return o.levelIds.includes(checklistLevelId.value!);
    });

    if (override != undefined) {
      return [
        {
          step: "Student Notes",
          instructions: override.instructions,
        },
      ];
    }
  }

  const filteredStudentNotes = studentNotes.filter((note) => {
    if (note.instructions == undefined) return false;

    if (note.instructions.length == 0) return false;

    if (note.instructions.length == 1) {
      if (note.instructions[0].insert == "\n" || note.instructions[0].insert == "") {
        return false;
      }
    }

    return true;
  });

  if (filteredStudentNotes.length == 0) return undefined;

  console.log(filteredStudentNotes);

  return filteredStudentNotes;
});

const { underlinedWords } = storeToRefs(thesaurusStore);

const criteriaSelfAssessmentSteps = computed(() => {
  return criteria.value?.selfAssessmentSteps ?? [];
});

const selfAssessmentStepAttributes = computed(() => {
  return criteriaSelfAssessmentSteps.value
    .map((step) => {
      return step.criteriaAttribute.attribute;
    })
    .filter((attribute) => {
      return attribute != undefined && attribute.includes("dressup");
    });
});

const dressUpsToTag = computed(() => {
  if (selfAssessmentStepAttributes.value.length == 0) return [];

  if (underlinedWords.value.length == 0) return [];

  return underlinedWords.value.filter((word) => {
    return Object.keys(word.attributes).some((attribute) => {
      return (
        attribute.toLowerCase().includes("dressup") &&
        selfAssessmentStepAttributes.value.includes(attribute)
      );
    });
  });
});

const selfAssessmentStore = useDocumentSelfAssessmentStore(documentId);
const { stepExpectedInstances } = storeToRefs(selfAssessmentStore);

const numRequiredInstances = computed(() => {
  if (criteriaSelfAssessmentSteps.value.length == 0) return 0;

  return criteriaSelfAssessmentSteps.value.reduce((acc, step) => {
    return acc + stepExpectedInstances.value(step);
  }, 0);
});

const showRequiredInstances = computed(() => {
  // If we're dealing with a dressup step, we need to check if the number of required instances is met
  if (selfAssessmentStepAttributes.value.length == 0) return false;

  return criteriaSelfAssessmentSteps.value.some((step) => {
    return step.criteriaAttribute.attribute.includes("dressup");
  });
});

const bannedWords = computed(() => {
  if (criteria.value?.isBannedWordsCriteria != true) {
    return undefined;
  }

  const metricId = criteria.value?.evaluationMetricId;
  const matchingMetric = evaluationMetrics.value.find((m) => m.id == metricId);

  const overrideKeys = Object.keys(matchingMetric?.overrides ?? {});

  let bannedWords: string[] = [];

  for (const overrideKey of overrideKeys) {
    const overrideValue = useChecklist.value?.overrides?.[overrideKey];

    if (overrideValue != undefined) {
      bannedWords.push(...overrideValue);
    }
  }

  console.log(bannedWords);

  const bannedWordsList = useExtractKeysFromBannedWords(bannedWords);

  return bannedWordsList;
});

const toggleIsStudentMarkedPositionComplete = (position: number) => {
  if (criteriaInstance.isStudentMarkedPositionComplete == undefined) {
    criteriaInstance.isStudentMarkedPositionComplete = {};
  }
  criteriaInstance.isStudentMarkedPositionComplete[position] =
    !criteriaInstance.isStudentMarkedPositionComplete[position];
  // save
  checklistStore.saveChecklist();
};

const isStudentMarkedPositionComplete = computed(() => {
  return (position: number) => {
    return (
      criteriaInstance.isStudentMarkedPositionComplete?.[position] ?? false
    );
  };
});

const numInstancesRequired = computed(() => {
  if (criteriaInstance.evaluationMethod == "document") return 1;

  if (criteriaInstance.evaluationMethod == "paragraph") {
    return criteriaParagraphPositionsData.value.length;
  }

  if (
    criteriaInstance.evaluationMethod == "instance" &&
    criteriaInstance.maxPoints != undefined &&
    criteriaInstance.pointsPerInstance != undefined
  ) {
    var pointsPerInstance = criteriaInstance.pointsPerInstance as any;

    if (pointsPerInstance == "") {
      pointsPerInstance = criteriaInstance.maxPoints;
    }

    return criteriaInstance.maxPoints / pointsPerInstance;
  }

  return 1;
});

const checkboxes = computed(() => {
  // add guard again numInstancesRequired being undefined or infinity
  if (
    numInstancesRequired.value == undefined ||
    numInstancesRequired.value == Infinity
  ) {
    return Array.from({ length: 1 }, (_, index) => index + 1);
  }

  return Array.from(
    { length: numInstancesRequired.value ?? 1 },
    (_, index) => index + 1
  );
});
</script>

<template>
  <div class="flex flex-col" v-auto-animate>
    <div
      :data-step-id="`${criteriaInstance.checklistSectionId}-${criteriaInstance.checklistCriteriaId}-${criteriaInstance.displayOrder}`"
      class="flex flex-row items-start justify-between hover:bg-light-default cursor-pointer py-1 px-2 min-h-[30px] rounded mb-1"
      :class="{
        'bg-success-default bg-opacity-10': isCriteriaComplete,
        ' text-danger-default': isIncomplete && isTeacherViewMode != true,
        'cursor-pointer': isTeacherViewMode == true,
        ' line-through': criteriaInstance.ignore == true,
      }"
      @click="onClickCriteria"
    >
      <ChecklistCriteriaQuickKeyButton
        v-if="showQuickKey == true && criteria != undefined"
        :criteria-instance="criteriaInstance"
        :criteria="criteria"
        :comments="comments"
      />

      <ChecklistCriteriaIgnoreButton
        v-if="isTeacherViewMode && allowIgnore == true && criteria != undefined"
        :criteria-instance="criteriaInstance"
        :criteria="criteria"
      />
      <ChecklistCriteriaWand
        v-if="
          isTeacherViewMode &&
          (hasAISuggestions || isAssignmentComponentCriteria)
        "
      />

      <span
        v-if="criteriaInstance.isCustom != true"
        class="grow leading-[30px] select-none inline"
      >
        {{ title }}
        <span
          v-if="studentInstructions != undefined"
          class="inline-flex items-center justify-center align-middle leading-none mb-[1px]"
        >
          <span
            class="inline-flex h-fit w-fit p-[2px] hover:bg-primary/20 rounded"
            :class="{
              'text-primary bg-primary/10': showStudentInstructions == true,
            }"
            @click.stop.prevent="toggleShowStudentInstructions"
          >
            <BaseIconCircleHelp />
          </span>
        </span>
      </span>
      <!-- <div
        v-if="isTeacherViewMode != true && showRequiredInstances == true"
        class="flex flex-row items-center justify-end leading-[30px] mr-2"
        :class="{
          'text-danger': dressUpsToTag.length > numRequiredInstances,
        }"
      >
        <span>
          {{ dressUpsToTag.length }}
        </span>
        <span> / </span>
        <span> {{ numRequiredInstances }} </span>
      </div> -->
      <div class="flex flex-row items-center">
        <div
          class="flex flex-row items-center justify-center h-[30px]"
          v-if="isTeacherViewMode != true && criteriaInstance.ignore != true"
        >
          <div
            v-if="checkboxes.length > 1"
            class="flex flex-row items-center justify-center gap-[2px] ml-4 mr-[2px]"
          >
            <BaseCheckbox
              v-for="i in checkboxes"
              :key="`${i}-${isStudentMarkedPositionComplete(i)}`"
              :model-value="isStudentMarkedPositionComplete(i)"
              @update:model-value="toggleIsStudentMarkedPositionComplete(i)"
            />
          </div>

          <BaseCheckbox
            v-if="checkboxes.length == 1"
            v-model="criteriaInstance.isStudentMarkedComplete"
            class="pointer-events-none"
          />
        </div>
      </div>
      <span
        v-if="criteriaInstance.isCustom == true"
        class="grow flex flex-row items-center"
      >
        <BaseIconButton
          v-if="isTeacherViewMode"
          color="danger"
          class="mr-1 -ml-1"
          @click="$emit('remove', criteriaInstance)"
        >
          <BaseIconClose />
        </BaseIconButton>
        <BaseTextInput
          v-if="isTeacherViewMode"
          v-model="criteriaInstance.labelOverride"
          class="grow !h-[35px]"
          placeholder="Label"
          @update:model-value="checklistStore.saveChecklist"
        />
        <span v-if="isTeacherViewMode != true">
          {{ title }}
        </span>
      </span>
      <span
        class="whitespace-nowrap ml-1 flex flex-row items-center justify-end h-[35px]"
      >
        <span
          v-if="criteriaInstance.ignore != true && isLoadingResults != true"
          class="w-full text-right"
        >
          <span
            v-if="requiresReview != true"
            class="w-full flex flex-row items-center justify-end"
          >
            <span
              v-if="showInput"
              class="test flex flex-row items-center"
              @click.stop
            >
              <div @click="onClickInput">
                <BaseScoreInput
                  :disabled="isDisabled"
                  :value="criteriaEarnedPoints"
                  :allow-quick-score="allowQuickScore != true"
                  :max-points="criteriaInstance.maxPoints"
                  outline-offset="0px"
                  @input="updateScore"
                />
              </div>
            </span>
          </span>
          <span
            v-if="isTeacherViewMode && requiresReview == true"
            class="text-warning-default italic pr-1"
          >
            please review
          </span>
        </span>
        <span
          v-if="
            isTeacherViewMode == true &&
            criteriaInstance.ignore != true &&
            isLoadingResults == true
          "
        >
          <icon name="line-md:loading-twotone-loop" size="20" />
        </span>
      </span>
    </div>
    <div v-if="bannedWords != undefined" class="text-body-2 px-[4px]">
      <div class="pl-1">{{ bannedWords.join(", ") }}</div>
    </div>
    <div v-if="showStudentInstructions == true">
      <ChecklistCriteriaInstructions
        v-if="
          studentInstructions != undefined && studentInstructions.length > 0
        "
        :student-instructions="studentInstructions"
      />
      <ChecklistCriteriaWordList
        v-if="
          showStudentInstructions == true &&
          checklistCriteria?.thesaurusWordList != undefined &&
          wordListById(checklistCriteria?.thesaurusWordList) != undefined
        "
        :criteria="checklistCriteria"
        :criteria-instance="criteriaInstance"
        :word-list="
          wordListById(checklistCriteria?.thesaurusWordList ?? '')?.words ?? []
        "
        class="mt-1 mb-4"
      />
    </div>
    <ChecklistCriteriaRelevantInstances
      v-if="isTeacherViewMode != true && criteria != undefined"
      :criteria-instance="criteriaInstance"
      :criteria="criteria"
    />
  </div>
</template>

<style scoped></style>
